import { motion } from 'framer-motion'
import { Button } from './Button'

interface EmptyStateProps {
  icon: string
  title: string
  description: string
  actionLabel?: string
  onAction?: () => void
  variant?: 'default' | 'compact'
}

export function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  variant = 'default',
}: EmptyStateProps) {
  if (variant === 'compact') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-8"
      >
        <motion.span
          className="text-4xl mb-3 block"
          animate={{ y: [0, -5, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        >
          {icon}
        </motion.span>
        <p className="text-text-secondary font-medium mb-1">{title}</p>
        <p className="text-sm text-text-muted">{description}</p>
        {actionLabel && onAction && (
          <Button onClick={onAction} size="sm" className="mt-4">
            {actionLabel}
          </Button>
        )}
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-12 px-6"
    >
      <motion.div
        className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-bg-card to-bg-surface rounded-2xl flex items-center justify-center"
        animate={{
          y: [0, -8, 0],
          rotate: [0, -3, 3, 0],
        }}
        transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
      >
        <span className="text-5xl">{icon}</span>
      </motion.div>

      <h3 className="font-display font-bold text-xl mb-2">{title}</h3>
      <p className="text-text-secondary max-w-xs mx-auto mb-4">{description}</p>

      {actionLabel && onAction && (
        <Button onClick={onAction}>{actionLabel}</Button>
      )}
    </motion.div>
  )
}

// Pre-defined empty states for common scenarios
export const EMPTY_STATES = {
  quests: {
    icon: '🗡️',
    title: 'No Quests Yet',
    description: "Your adventure awaits! Add your first quest and start earning XP.",
  },
  habits: {
    icon: '🌱',
    title: 'No Habits Yet',
    description: 'Build positive habits and watch your streaks grow!',
  },
  routines: {
    icon: '☀️',
    title: 'No Routines Yet',
    description: 'Create morning and evening routines to structure your day.',
  },
  projects: {
    icon: '👹',
    title: 'No Boss Battles',
    description: 'Break down big projects into epic boss fights!',
  },
  completed: {
    icon: '🏆',
    title: 'All Done!',
    description: "You've completed everything. Time to celebrate!",
  },
  search: {
    icon: '🔍',
    title: 'No Results',
    description: "We couldn't find what you're looking for.",
  },
}
